import { useState } from 'react';
import { HeroStatTile } from '../components/HeroStatTile';
import { SkelBar, SkelBadge, SkeletonPreviewLabel } from '../components/Skeleton';

/**
 * RFP Pursuit Management visual shell. Internal admin view, distinct from
 * the recipient /rfp-view flow. Ports v0.7 RFPPage (index.html ~3575).
 *
 * 7.1d structure - matches v0.7 exactly:
 *   - Gates (index.html line 3640, 3650 `GATE_DEFS`):
 *     Qualify (1) / Strategy (2) / Delivery (3) / NBR (4) / Submit (5)
 *   - Top controls (index.html line 4260-4263):
 *     Library button / Archive toggle / + New pursuit (primary)
 *   - File drop zone (index.html line 4327):
 *     Drag-drop or click to upload RFP document. Generates Pre-Read.
 *   - Each gate card has Pre-Read status indicator + advance/share controls
 *
 * All controls disabled. Drop zone is non-interactive in 7.1d.
 */
export function RfpMgmt() {
  const [view, setView] = useState<'active' | 'library' | 'archive'>('active');

  // v0.7 GATE_DEFS (index.html line 3650). Colors approximate v0.7 funnel palette.
  const gates = [
    { name: 'Qualify', color: '#0F6E56', count: 0, cardCount: 2 },
    { name: 'Strategy', color: '#D97706', count: 0, cardCount: 1 },
    { name: 'Delivery', color: '#4338CA', count: 0, cardCount: 2 },
    { name: 'NBR', color: '#2563EB', count: 0, cardCount: 1 },
    { name: 'Submit', color: '#7C3AED', count: 0, cardCount: 1 },
  ];

  return (
    <div>
      <div className="bdse-hero">
        <div className="bdse-hero-row">
          <div>
            <h1 className="bdse-hero-title">RFP Pursuit Mgmt</h1>
            <p className="bdse-hero-subtitle">
              Qualify, strategize, deliver, NBR, submit
            </p>
          </div>

          {/* v0.7 top controls (index.html line 4260-4263) */}
          <div className="bdse-hero-actions">
            <button
              type="button"
              onClick={() => setView('library')}
              className={`bdse-hero-btn-ghost ${view === 'library' ? 'bdse-hero-btn-ghost-active' : ''}`}
              title="View library of past RFP pursuits"
            >
              Library
            </button>
            <button
              type="button"
              onClick={() => setView(view === 'archive' ? 'active' : 'archive')}
              className={`bdse-hero-btn-ghost ${view === 'archive' ? 'bdse-hero-btn-ghost-active' : ''}`}
              title="Toggle archived RFP pursuits"
            >
              {view === 'archive' ? 'Active' : 'Archive'}
            </button>
            <button
              type="button"
              disabled
              title="Coming in Stream 3D (RFP creation flow)"
              className="bdse-hero-btn-primary"
            >
              + New pursuit
            </button>
          </div>
        </div>

        <div className="bdse-hero-stats">
          <HeroStatTile label="Active" value="0" sub="in pipeline" icon="briefcase" />
          <HeroStatTile label="Qualify" value="0" sub="gate 1" icon="check" />
          <HeroStatTile label="Strategy" value="0" sub="gate 2" icon="target" />
          <HeroStatTile label="Delivery" value="0" sub="gate 3" icon="flag" />
          <HeroStatTile label="NBR" value="0" sub="gate 4" icon="users" />
          <HeroStatTile label="Submit" value="0" sub="gate 5" icon="document" />
        </div>
      </div>

      {/* Library / Archive return-to-active control */}
      {view !== 'active' && (
        <div className="bdse-rfp-back-row">
          <button
            type="button"
            onClick={() => setView('active')}
            className="bdse-btn-secondary-small"
          >
            ← Back to active
          </button>
          <span className="bdse-rfp-view-label">
            {view === 'library' ? 'Library view' : 'Archived view'}
          </span>
        </div>
      )}

      {/* File drop zone (v0.7 line 4327) */}
      <div className="bdse-rfp-dropzone" aria-disabled="true">
        <div className="bdse-rfp-dropzone-icon" aria-hidden="true">
          {'\u{1F4C4}'}
        </div>
        <div className="bdse-rfp-dropzone-body">
          <div className="bdse-rfp-dropzone-title">
            Drop an RFP document or paste text to generate a Pre-Read
          </div>
          <p className="bdse-rfp-dropzone-sub">
            Stream 3D wires file ingestion, text extraction, and LLM-driven
            Pre-Read generation. Supported: PDF, DOCX, TXT.
          </p>
        </div>
        <button
          type="button"
          disabled
          title="Coming in Stream 3D"
          className="bdse-btn-secondary-small"
        >
          Generate Pre-Read
        </button>
      </div>

      <SkeletonPreviewLabel text="Sample preview - RFP pursuits flow left-to-right through the five gates" />

      <div className="bdse-rfp-board">
        {gates.map((gate, idx) => (
          <div key={gate.name} className="bdse-rfp-col">
            <header
              className="bdse-rfp-col-header"
              style={{ borderTop: `3px solid ${gate.color}` }}
            >
              <span className="bdse-rfp-col-title">
                <span className="bdse-rfp-col-num">{idx + 1}</span>
                {gate.name}
              </span>
              <span className="bdse-rfp-col-count">{gate.count}</span>
            </header>

            <div className="bdse-rfp-card-list">
              {Array.from({ length: gate.cardCount }).map((_, i) => (
                <article key={i} className="bdse-rfp-card bdse-skeleton-card">
                  <div className="bdse-rfp-card-header">
                    <SkelBar w="78%" h={11} />
                  </div>
                  <div className="bdse-rfp-card-meta">
                    <SkelBadge w={56} h={14} />
                    <SkelBar w={42} h={8} />
                  </div>
                  <div className="bdse-rfp-card-body">
                    <SkelBar w="100%" h={8} style={{ marginTop: 4 }} />
                    <SkelBar w="68%" h={8} style={{ marginTop: 4 }} />
                  </div>
                  <div className="bdse-rfp-card-foot">
                    <span className="bdse-rfp-card-preread">
                      <span
                        className="bdse-rfp-card-preread-dot"
                        aria-hidden="true"
                      />
                      Pre-Read
                    </span>
                    <span className="bdse-rfp-card-owner-dot" aria-hidden="true" />
                  </div>
                </article>
              ))}
              <div className="bdse-rfp-col-drop">
                Drag-drop wires in Stream 3D
              </div>
            </div>
          </div>
        ))}
      </div>

      <p className="bdse-rfp-foot-note">
        Recipient assignment links generate from each pursuit and surface in
        the Stream 7.3 recipient flow. SMD approvals via magic link gate
        advancement between Qualify and Strategy.
      </p>
    </div>
  );
}
